import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Building2, Briefcase, Check, LayoutDashboard, Users, LineChart, Calendar } from 'lucide-react';
import { cn } from '../../utils/cn';
import { authStaggerContainer, authStaggerItem, authEase } from './authMotion';
import { workspacePreviews } from './authData';

export type AccountType = 'business' | 'ca';

interface AccountTypeCardsProps {
  value: AccountType;
  onChange: (type: AccountType) => void;
  error?: string | undefined;
}

const OPTIONS: {
  id: AccountType;
  title: string;
  description: string;
  icon: React.ElementType;
}[] = [
  {
    id: 'business',
    title: 'Business Workspace',
    description: 'For founders, startups, and growing companies managing day-to-day finance operations.',
    icon: Building2,
  },
  {
    id: 'ca',
    title: 'CA Workspace',
    description: 'For chartered accountants and firms managing multiple client portfolios.',
    icon: Briefcase,
  },
];

const previewIcons = {
  business: [LayoutDashboard, LineChart, Check],
  ca: [Users, Calendar, LineChart],
} as const;

export const AccountTypeCards: React.FC<AccountTypeCardsProps> = ({ value, onChange, error }) => {
  const reduceMotion = useReducedMotion();
  const [hovered, setHovered] = useState<AccountType | null>(null);
  const previewType = hovered ?? value;
  const preview = workspacePreviews[previewType];

  return (
    <motion.div
      className="space-y-3"
      variants={authStaggerContainer}
      initial="hidden"
      animate="visible"
    >
      <fieldset>
        <legend className="sr-only">Workspace type</legend>
        <motion.div className="grid gap-3 sm:grid-cols-2" variants={authStaggerContainer}>
          {OPTIONS.map((opt) => {
            const selected = value === opt.id;
            const Icon = opt.icon;
            const isHover = hovered === opt.id;

            return (
              <motion.button
                key={opt.id}
                type="button"
                variants={authStaggerItem}
                onClick={() => onChange(opt.id)}
                onMouseEnter={() => setHovered(opt.id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(opt.id)}
                onBlur={() => setHovered(null)}
                whileTap={reduceMotion ? {} : { scale: 0.995 }}
                transition={{ duration: 0.2 }}
                className={cn(
                  'relative flex flex-col items-start rounded-xl border p-3 text-left transition-colors duration-200 sm:p-3.5',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
                  selected
                    ? 'border-blue-600 bg-blue-50/40 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300',
                  isHover && !selected && 'border-blue-200/80 bg-slate-50/50'
                )}
                aria-pressed={selected}
              >
                {selected ? (
                  <motion.span
                    className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white"
                    initial={reduceMotion ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                    aria-hidden
                  >
                    <Check className="h-3 w-3" />
                  </motion.span>
                ) : null}

                <motion.span
                  className={cn(
                    'mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-colors duration-200',
                    selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                  )}
                  animate={isHover && !reduceMotion ? { y: -1 } : { y: 0 }}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                </motion.span>

                <span className="text-sm font-semibold text-gray-900">{opt.title}</span>
                <span className="mt-1 text-xs leading-relaxed text-gray-500">{opt.description}</span>
              </motion.button>
            );
          })}
        </motion.div>
      </fieldset>

      <AnimatePresence mode="wait">
        <motion.div
          key={previewType}
          initial={reduceMotion ? false : { opacity: 0, y: 6, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          {...(reduceMotion ? {} : { exit: { opacity: 0, y: -4, height: 0 } })}
          transition={{ duration: 0.32, ease: authEase }}
          className="hidden overflow-hidden rounded-xl border border-slate-200/80 bg-slate-50/80 p-3 md:block"
        >
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Operational preview · {preview.title}
          </p>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {preview.metrics.map((m) => (
              <span
                key={m}
                className="rounded-md border border-white bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 shadow-sm"
              >
                {m}
              </span>
            ))}
          </div>
          <ul className="space-y-1">
            {preview.feed.map((line, i) => {
              const Icons = previewIcons[previewType];
              const PIcon = Icons[i % Icons.length]!;
              return (
                <li key={line} className="flex items-center gap-2 text-[11px] text-slate-600">
                  <PIcon className="h-3 w-3 shrink-0 text-blue-500" aria-hidden />
                  {line}
                </li>
              );
            })}
          </ul>
        </motion.div>
      </AnimatePresence>

      {error ? (
        <p role="alert" className="text-sm text-red-500">
          {error}
        </p>
      ) : null}
    </motion.div>
  );
};
